import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAdminTenants, useCreateAdminNumberRequest } from "@/features/admin";
import {
  NUMBER_REQUEST_KINDS,
  type NumberRequestKind,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Open a request for a client we are setting up ourselves.
 *
 * The client's own page is the usual way in, and it asks one question (which
 * route in) before dropping them on the detail page to fill the rest. This one
 * asks two — which tenant, and which route — because from here the tenant is
 * the thing that has to be got right, and everything else is editable on the
 * page it opens.
 *
 * The country and prefix are offered anyway: an operator opening a request on
 * behalf of a client on the phone already has them, and typing them now saves
 * a second pass. Both are optional, like everywhere else.
 */
export const CreateAdminNumberRequestDialog = () => {
  const { t } = useTranslation("numberRequests");
  const navigate = useNavigate();

  const { data: tenants = [] } = useAdminTenants();

  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [kind, setKind] = useState<NumberRequestKind>("new_number");
  const [country, setCountry] = useState("FR");
  const [desiredPrefix, setDesiredPrefix] = useState("");
  const [notes, setNotes] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");

  const create = useCreateAdminNumberRequest(clientId);

  const reset = () => {
    setClientId("");
    setKind("new_number");
    setCountry("FR");
    setDesiredPrefix("");
    setNotes("");
    setNotificationEmail("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!clientId) {
      toast.error("Choisissez le tenant pour qui ouvrir la demande.");
      return;
    }

    try {
      const request = await create.mutateAsync({
        kind,
        country: country.trim().toUpperCase(),
        desired_prefix: desiredPrefix.trim(),
        notes: notes.trim(),
        notification_email: notificationEmail.trim(),
      });
      toast.success("Demande ouverte");
      setOpen(false);
      reset();
      navigate(`/admin/number-requests/${clientId}/${request.id}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Création impossible",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          Ouvrir une demande
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Ouvrir une demande de numéro</DialogTitle>
            <DialogDescription>
              Pour un client que nous équipons nous-mêmes. La demande lui
              appartient : il la verra sur sa propre page « Canaux ».
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tenant">
                Tenant <span className="text-red-500">*</span>
              </Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger id="tenant">
                  <SelectValue placeholder="Choisir un tenant…" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name || tenant.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="kind">Type de demande</Label>
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as NumberRequestKind)}
              >
                <SelectTrigger id="kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NUMBER_REQUEST_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`numberRequests.kinds.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {t(`numberRequests.kindSwitch.${kind}`)}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="country">Pays</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                  placeholder="FR"
                  maxLength={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prefix">Préfixe souhaité</Label>
                <Input
                  id="prefix"
                  value={desiredPrefix}
                  onChange={(event) => setDesiredPrefix(event.target.value)}
                  placeholder="0491"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-email">E-mail de notification</Label>
              <Input
                id="notification-email"
                type="email"
                value={notificationEmail}
                onChange={(event) => setNotificationEmail(event.target.value)}
                placeholder="contact@client.fr"
              />
              <p className="text-muted-foreground text-xs">
                Reçoit les liens de suivi des livraisons et l'annonce de la mise
                en service. Vide : les propriétaires du compte, s'il y en a.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Précisions</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Contexte de la commande, ce que le client a demandé au téléphone…"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={create.isPending || !clientId}>
              {create.isPending ? "Création…" : "Ouvrir la demande"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
